/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  ListItem,
  ListItemButton,
  Typography,
  useTheme,
} from '@mui/material';
import type { TFunction } from 'i18next';
import type { FC } from 'react';
import React from 'react';
import type { RowComponentProps } from 'react-window';
import { List } from 'react-window';
import { ToggleMenu } from '@opengeoweb/shared';
import {
  ExitDomain,
  Options,
  ChevronDown,
  Remove,
  DrawPolygon,
} from '@opengeoweb/theme';
import {
  DEFAULT_ADD_BUTTON_HEIGHT,
  DEFAULT_OBJECT_MANAGER_MIN_SIZE,
  DEFAULT_OBJECT_MANAGER_TAB_HEIGHT,
} from './Objects';
import type { AreaListItem } from '../../store/warningsDrawings/types';
import { useWarningsTranslation } from '../../utils/i18n';

const AREA_ITEM_SIZE = 38;
const SELECTED_AREA_ITEM_SIZE = 32;

export type SelectedAreaFeatureNamesByAreaId = Record<string, string[]>;

export const Areas: FC<{
  areas: AreaListItem[];
  onHoverArea?: (area: AreaListItem) => void;
  onHoverAreaEnd?: () => void;
  onClickArea: (area: AreaListItem) => void;
  //   onClickEdit: (areaId: string) => void;
  //   onClickClear: (areaId: string) => void;
  //   onClickShare: (area: AreaListItem) => void;
  activeArea: string;
  selectedAreaFeatureNamesByAreaId?: SelectedAreaFeatureNamesByAreaId;
  isLoading?: boolean;
  dialogWidth?: number;
}> = ({
  areas,
  onHoverArea = (): void => {},
  onHoverAreaEnd = (): void => {},
  onClickArea,
  // onClickEdit,
  // onClickClear,
  // onClickShare,
  activeArea,
  selectedAreaFeatureNamesByAreaId = {},
  isLoading = false,
  dialogWidth = DEFAULT_OBJECT_MANAGER_MIN_SIZE.width,
}) => {
  const { t } = useWarningsTranslation();
  const [expandedSection, setExpandedSection] =
    React.useState<string>(activeArea);

  React.useEffect(() => {
    if (selectedAreaFeatureNamesByAreaId[activeArea]?.length) {
      setExpandedSection(activeArea);
    }
  }, [activeArea, selectedAreaFeatureNamesByAreaId]);

  const getItemSize = (index: number): number => {
    if (expandedSection === areas[index].id) {
      const selectedFeatureNames =
        selectedAreaFeatureNamesByAreaId[areas[index].id] ?? [];

      return (
        AREA_ITEM_SIZE +
        Math.max(selectedFeatureNames.length, 1) * SELECTED_AREA_ITEM_SIZE +
        24
      );
    }
    return AREA_ITEM_SIZE;
  };

  const onToggleExpand = (areaId: string): void => {
    expandedSection === areaId
      ? setExpandedSection('')
      : setExpandedSection(areaId);
  };

  return (
    <Box
      sx={{
        height: `calc(100% - ${DEFAULT_OBJECT_MANAGER_TAB_HEIGHT}px)`,
        width: '100%',
        paddingTop: '8px',
      }}
    >
      {!isLoading && areas.length === 0 ? (
        <Typography
          sx={{ paddingLeft: '16px', paddingTop: '8px' }}
          variant="body1"
        >
          {t('object-manager-no-areas')}
        </Typography>
      ) : (
        <List
          rowComponent={RowComponent}
          rowCount={areas.length}
          rowHeight={(index): number => {
            return getItemSize(index);
          }}
          rowProps={{
            areas,
            onHoverArea,
            onHoverAreaEnd,
            onClickArea,
            // onClickEdit,
            // onClickClear,
            // onClickShare,
            activeArea,
            selectedAreaFeatureNamesByAreaId,
            expandedSection,
            onToggleExpand,
            t,
          }}
          style={{
            width: '100%',
            height: `calc(100% - ${DEFAULT_ADD_BUTTON_HEIGHT}px)`,
            containerType: 'inline-size',
            containerName: 'areaList',
          }}
        />
      )}
    </Box>
  );
};

const RowComponent = ({
  index,
  style,
  areas,
  onHoverArea,
  onHoverAreaEnd,
  onClickArea,
  //   onClickEdit,
  //   onClickDelete,
  //   onClickShare,
  onToggleExpand,
  expandedSection,
  activeArea,
  selectedAreaFeatureNamesByAreaId,
  t,
}: RowComponentProps<{
  areas: AreaListItem[];
  onHoverArea: (area: AreaListItem) => void;
  onHoverAreaEnd: () => void;
  onClickArea: (area: AreaListItem) => void;
  //   onClickEdit: (areaId: string) => void;
  //   onClickClear: (areaId: string) => void;
  //   onClickShare: (area: AreaListItem) => void;
  expandedSection: string;
  onToggleExpand: (areaId: string) => void;
  activeArea: string;
  selectedAreaFeatureNamesByAreaId: SelectedAreaFeatureNamesByAreaId;
  t: TFunction;
}>): React.ReactElement => {
  const theme = useTheme();
  const area = areas[index];
  const selectedFeatureNames = selectedAreaFeatureNamesByAreaId[area.id] ?? [];

  return (
    <Accordion
      disableGutters={true}
      expanded={expandedSection === area.id}
      sx={{
        '.MuiAccordionSummary-content': {
          padding: '0px',
          margin: '0px',
        },
        '&.MuiAccordion-root': {
          boxShadow: 0,
        },
      }}
    >
      <AccordionSummary
        component="div"
        data-testid={`area-summary-${area.id}`}
        expandIcon={
          <ChevronDown
            onClick={() => {
              onToggleExpand(area.id);
            }}
            style={{
              fill: `${theme.palette.geowebColors.buttons.icon.default.color}`,
            }}
          />
        }
        onMouseEnter={(): void => onHoverArea(area)}
        onMouseLeave={(): void => onHoverAreaEnd()}
        sx={{
          '&.MuiAccordionSummary-root:hover': {
            backgroundColor: 'geowebColors.cards.cardContainerMouseOver',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <ListItem
            disablePadding
            key={area.id}
            secondaryAction={
              <Grid container rowSpacing={1}>
                <Grid className="areaActions">
                  <ToggleMenu
                    aria-label={t('object-manager-button-share')}
                    buttonIcon={<Options />}
                    menuItems={[
                      {
                        text: t('area-manager-share-menu-public-warning'),
                        action: (): void => {},
                        icon: <ExitDomain />,
                        isDisabled: true,
                      },
                    ]}
                    menuPosition="bottom"
                    menuTitle={t('area-manager-share-menu')}
                    secondaryMenuItems={[
                      {
                        text: t('area-manager-share-menu-edit'),
                        action: (): void => {},
                        icon: <DrawPolygon />,
                        isDisabled: true,
                      },
                      {
                        text: t('area-manager-share-menu-clear'),
                        action: (): void => {},
                        icon: <Remove />,
                        isDisabled: true,
                      },
                    ]}
                    secondaryMenuTitle={t('area-manager-share-menu-options')}
                    tooltipTitle={t('area-manager-button-share')}
                  />
                </Grid>
              </Grid>
            }
          >
            <ListItemButton
              onClick={(): void => {
                onClickArea(area);
              }}
              selected={activeArea === area.id}
              sx={{
                padding: 1,
                height: '36px',
                '&.Mui-selected': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Typography
                sx={{
                  lineHeight: '16px',
                  maxHeight: '32px',
                  overflow: 'hidden',
                }}
                variant="caption"
              >
                {area.objectName}
              </Typography>
            </ListItemButton>
          </ListItem>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: '0 8px 8px 24px' }}>
        {selectedFeatureNames.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {selectedFeatureNames.map((selectedFeatureName) => (
              <Box
                key={`${selectedFeatureName}-${index}`}
                sx={{
                  alignItems: 'center',
                  backgroundColor: 'primary.main',
                  borderRadius: '2px',
                  color: 'primary.contrastText',
                  display: 'flex',
                  minHeight: `${SELECTED_AREA_ITEM_SIZE - 4}px`,
                  padding: '4px 8px',
                }}
              >
                <Typography
                  sx={{
                    lineHeight: '16px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  variant="caption"
                >
                  {selectedFeatureName}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2">
            {t('object-manager-select-areas')}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
